import './perks-account.component.scss';

import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { LegacyAuthModalState } from '../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../auth/services/authentication-flow.service';
import {
  selectAccountSettings,
  selectCustomer,
  selectCustomerLoaded,
  selectCustomerLoading,
} from '../../../auth/store/auth.selectors';
import { initializeCustomer, loadAccountSettings } from '../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { IconCalendar } from '../../../common/icons/icon-calendar';
import { IconLogout } from '../../../common/icons/icon-logout';
import { IconPencil } from '../../../common/icons/icon-pencil';
import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag.hook';
import { FeatureFlagName } from '../../../feature-flags/models/feature-flag-names.enum';
import { useTranslation } from '../../../i18n';
import { MembershipAccountInfo } from '../../../membership/components/membership-account-info/membership-account-info.component';
import { MembershipState } from '../../../membership/models/membership.model';
import { selectMemberships } from '../../../membership/store/membership.selectors';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { toastService } from '../../../toast/toast.service';
import { type EmailPreference, EmailPreferencesType } from '../../model/user.interface';
import { selectUser, selectUserLoaded, selectUserUpdating } from '../../store/perks.selectors';
import { loadUser, updateUser, uploadAvatar } from '../../store/perks.thunk';
import { useDisplayIdentity } from '../../use-display-identity.hook';
import { PerksAvatar } from '../perks-avatar/perks-avatar.component';
import { PROFILE_BADGE_SIZE_PX } from '../perks-badge/perks-badge.component';
import { PerksWalletCard } from '../perks-wallet-card/perks-wallet-card.component';

interface UserFormData {
  username: string;
  countryCode: string;
  phone: string;
  birthday: string;
  zipCode: string;
  emailPreferences: EmailPreference[];
}

const EMAIL_PREFERENCE_ORDER: string[] = [
  EmailPreferencesType.POST_NOTIFICATIONS,
  EmailPreferencesType.COMMENT_NOTIFICATIONS,
];

const byDisplayOrder = (preferences: EmailPreference[]): EmailPreference[] => {
  const rank = (type: string) => {
    const index = EMAIL_PREFERENCE_ORDER.indexOf(type);
    return index === -1 ? EMAIL_PREFERENCE_ORDER.length : index;
  };
  return [...preferences].sort((a, b) => rank(a.type) - rank(b.type));
};

export const PerksAccountComponent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const memberships = useAppSelector(selectMemberships);
  const customer = useAppSelector(selectCustomer);
  const customerLoading = useAppSelector(selectCustomerLoading);
  const customerLoaded = useAppSelector(selectCustomerLoaded);
  const user = useAppSelector(selectUser);
  const userLoaded = useAppSelector(selectUserLoaded);
  const userUpdating = useAppSelector(selectUserUpdating);
  const submissionsEnabled = useFeatureFlag(FeatureFlagName.COMMUNITY_SUBMISSIONS);
  const officialAuthorsEnabled = useFeatureFlag(FeatureFlagName.COMMUNITY_OFFICIAL_AUTHORS);
  const notificationEmailsEnabled = useFeatureFlag(FeatureFlagName.COMMUNITY_NOTIFICATION_EMAILS);
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};
  const fileInputRef = useRef<HTMLInputElement>(null);
  // SNGL-7327. A user linked to an official author is shown as that author, and the fan-facing
  // parts of this page -- the editable display name and photo, the membership card, the Join call
  // to action -- are not theirs. The Author itself is managed in the dashboard and is never
  // written from here, so `official` only ever hides fan controls; it never redirects a save.
  const identity = useDisplayIdentity(user);
  // any contract at all, failed included, so a test join stays cancellable
  const hasContract = !!memberships?.some((m) => !!m.contract);

  const [showEdit, setShowEdit] = useState(false);
  const [_showManageMembership, setShowManageMembership] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [avatarImageLoading, setAvatarImageLoading] = useState(false);
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    countryCode: '',
    phone: '',
    birthday: '',
    zipCode: '',
    emailPreferences: [],
  });

  const todayMinus18 = (() => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today.toISOString().split('T')[0];
  })();

  const anyActiveMemberships =
    (memberships?.length ?? 0) > 0 &&
    memberships?.some((m) => m.state === MembershipState.PUBLISHED);

  // Initialize on mount
  useEffect(() => {
    dispatch(loadAccountSettings());

    const params = new URLSearchParams(window.location.search);
    if (params.get('showEdit') === 'true') {
      setShowEdit(true);
    }
    if (params.get('showManage') === 'true') {
      setShowManageMembership(true);
    }

    if (!customerLoaded) {
      dispatch(initializeCustomer());
    }

    if (!userLoaded) {
      dispatch(loadUser());
    }
  }, []);

  useEffect(() => {
    if (user) {
      const phoneParts = splitPhoneNumber(user.phone);
      setFormData({
        username: user.username || '',
        countryCode: phoneParts?.countryCode || '',
        phone: phoneParts?.localNumber || '',
        birthday: user.birthday || '',
        zipCode: user.zipCode || '',
        emailPreferences: visiblePreferences(user.emailPreferences ?? []).map((e) => ({
          id: e.id,
          type: e.type,
          enabled: e.enabled,
        })),
      });
    }
  }, [user]);

  const handleInputChange = (
    field: keyof Omit<UserFormData, 'emailPreferences'>,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmailPrefChange = (index: number, enabled: boolean) => {
    setFormData((prev) => ({
      ...prev,
      emailPreferences: prev.emailPreferences.map((pref, i) =>
        i === index ? { ...pref, enabled } : pref
      ),
    }));
  };

  const handleUploadAvatar = async (file: File | undefined) => {
    if (!file || !user) return;
    const filename = file.name.replace(/[^a-zA-Z0-9.]/g, '-');
    setIsUploading(true);
    const result = await dispatch(uploadAvatar({ file, filename }));
    setIsUploading(false);
    if (uploadAvatar.rejected.match(result)) {
      toastService.error(t('common.error'), t('perks.errorUploadAvatar'));
    } else if (uploadAvatar.fulfilled.match(result)) {
      setAvatarImageLoading(true);
    }
  };

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const handleCancelEdit = (e: MouseEvent) => {
    e.preventDefault();
    e.stopImmediatePropagation();
    setShowEdit(false);
  };

  const handleOpenLoginModal = async () => {
    await authenticationFlowService.initiateAuthentication({
      state: LegacyAuthModalState.LOGIN,
      allowedStates: [LegacyAuthModalState.LOGIN, LegacyAuthModalState.RESET_PASSWORD],
    });
  };

  const handleSave = async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!user) return;

    let fullPhoneNum: string | undefined;
    if (formData.phone) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      const cc = formData.countryCode.startsWith('+')
        ? formData.countryCode
        : `+${formData.countryCode}`;
      fullPhoneNum = `${cc}${phoneDigits}`;
    }

    const result = await dispatch(
      updateUser({
        username: formData.username,
        imageFileSetId: user.imageFileSetId,
        emailPreferences: formData.emailPreferences,
        phone: fullPhoneNum ?? '',
        birthday: formData.birthday,
        zipCode: formData.zipCode,
      })
    );

    // Close edit mode on success
    if (updateUser.fulfilled.match(result)) {
      setShowEdit(false);
    }
  };

  const splitPhoneNumber = (phone: string | undefined) => {
    if (!phone) return null;

    const trimmed = phone.replace(/\D/g, '');
    if (trimmed.length === 0) return null;

    // If phone is 10 digits or less, assume no country code
    if (trimmed.length <= 10) {
      return {
        countryCode: '+1',
        localNumber: formatPhone(trimmed),
      };
    }

    const localNumber = trimmed.slice(-10);
    const countryCode = phone.slice(0, phone.length - 10);

    return {
      countryCode,
      localNumber: formatPhone(localNumber),
    };
  };

  const formatPhone = (raw: string) => {
    let value = raw.replace(/\D/g, '');
    if (value.length > 10) value = value.slice(0, 10);

    const parts = [];
    if (value.length >= 1) parts.push('(' + value.slice(0, 3));
    if (value.length >= 4) parts.push(') ' + value.slice(3, 6));
    if (value.length >= 7) parts.push('-' + value.slice(6, 10));

    return parts.join('');
  };

  const handlePhoneInput = (e: Event) => {
    const formatted = formatPhone((e.target as HTMLInputElement).value);
    handleInputChange('phone', formatted);
  };

  const readOnlyPhone = (phone: string): string => {
    const parts = splitPhoneNumber(phone);
    return parts ? `${parts.countryCode} ${parts.localNumber}` : phone;
  };

  const humanize = (str: string | undefined) => {
    return (
      str
        ?.toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l: string) => l.toUpperCase()) || ''
    );
  };

  const emailPreferenceLabel = (type: string | undefined) => {
    const key = `perks.emailPreference.${type}`;
    const label = t(key);
    return label === key ? humanize(type) : label;
  };

  /**
   * Preferences to show, in a fixed order. Comment notifications stay hidden until the
   * account has the flag on -- the row still exists, we just don't offer the toggle.
   */
  const visiblePreferences = (preferences: EmailPreference[]): EmailPreference[] =>
    byDisplayOrder(preferences).filter(
      (preference) =>
        notificationEmailsEnabled || preference.type !== EmailPreferencesType.COMMENT_NOTIFICATIONS
    );

  const translateOrEmpty = (key: string) => {
    const value = t(key);
    return value === key ? '' : value;
  };

  const emailPreferenceDescription = (type: string | undefined) => {
    const base = `perks.emailPreference.${type}.description`;
    // an official author publishes, a fan submits, and without submissions neither applies
    if (officialAuthorsEnabled && user?.officialAuthor) {
      return translateOrEmpty(`${base}.author`) || translateOrEmpty(base);
    }
    if (submissionsEnabled) {
      return translateOrEmpty(`${base}.submissions`) || translateOrEmpty(base);
    }
    return translateOrEmpty(base);
  };

  if (customerLoading) {
    return null;
  }

  return (
    <div class="single-perks-account-wrapper">
      {customer?.customerId && (
        <div class="my-account-wrapper">
          <div class="my-account-header">
            <h3 class="h3 single-h3">{t('perks.myAccount')}</h3>
            <div class="actions">
              <span>
                <a href="/account/logout">
                  <IconLogout />
                  <span>{t('perks.logout')}</span>
                </a>
              </span>
            </div>
          </div>

          <div class="my-account-info">
            {userUpdating && (
              <section class="single-saving-container">
                <SpinnerComponent />
                {isUploading && <p class="single-uploading-text">{t('common.uploading')}</p>}
              </section>
            )}

            <div class={`customer-info ${showEdit ? 'editing' : ''}`}>
              {!showEdit ? (
                <div class="single-profile-card">
                  <div class="single-profile-card-header">
                    {user && (
                      <PerksAvatar user={user} size={90} badgeSize={PROFILE_BADGE_SIZE_PX} />
                    )}

                    {customer && (
                      <>
                        <div class="single-user-info">
                          <h4 class="h4 single-h4">
                            {identity.official
                              ? identity.name
                              : user?.username || `${customer.firstName} ${customer.lastName}`}
                          </h4>
                          {identity.official && (
                            <p class="single-artist-profile-note">
                              {t('perks.artistProfileManaged')}
                            </p>
                          )}
                          <p>{customer.email}</p>
                        </div>
                        <div class="single-mobile-edit-toggle">
                          <button
                            class="button btn"
                            aria-label={t('perks.updateProfile')}
                            onClick={() => setShowEdit(true)}
                          >
                            <IconPencil />
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  {customer && (
                    /* Read-only profile fields (SNGL-7029): show the data
                       we have without requiring edit mode. Only fields the
                       admin enabled render, and only when they have a value. */
                    <div class="single-profile-details">
                      {accountSettings?.showPhoneField && !!user?.phone && (
                        <div class="single-profile-detail">
                          <span class="single-profile-detail-label">{t('auth.phoneNumber')}</span>
                          <span>{readOnlyPhone(user.phone)}</span>
                        </div>
                      )}
                      {accountSettings?.showBirthdayField && !!user?.birthday && (
                        <div class="single-profile-detail">
                          <span class="single-profile-detail-label">{t('auth.birthday')}</span>
                          <span>{user.birthday}</span>
                        </div>
                      )}
                      {accountSettings?.showZipCodeField && !!user?.zipCode && (
                        <div class="single-profile-detail">
                          <span class="single-profile-detail-label">
                            {t('auth.zipcodePlaceholder')}
                          </span>
                          <span>{user.zipCode}</span>
                        </div>
                      )}
                      {(user?.emailPreferences?.some((p) => p.enabled) ?? false) && (
                        <div class="single-profile-detail">
                          <span class="single-profile-detail-label">
                            {t('perks.emailPreferences')}
                          </span>
                          <span>
                            {visiblePreferences(user?.emailPreferences ?? [])
                              .filter((p) => p.enabled)
                              .map((p) => emailPreferenceLabel(p.type))
                              .join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="file"
                    accept=".jpg,.jpeg,.png,.gif"
                    hidden
                    style="display: none !important"
                    onChange={(e) => handleUploadAvatar((e.target as HTMLInputElement).files?.[0])}
                  />

                  <div class="single-avatar-wrapper">
                    {user && (
                      <PerksAvatar
                        user={user}
                        size={90}
                        badgeSize={PROFILE_BADGE_SIZE_PX}
                        onImageLoad={() => setAvatarImageLoading(false)}
                      />
                    )}
                    {avatarImageLoading && (
                      <div class="single-avatar-loading-overlay">
                        <SpinnerComponent />
                      </div>
                    )}
                    {/* the fan photo is not what a linked author is shown as */}
                    {!identity.official && (
                      <span class="single-edit-container" onClick={handleFileInputClick}>
                        <button class="button btn" aria-label={t('a11y.uploadAvatar')}>
                          <IconPencil width={11} height={11} />
                        </button>
                      </span>
                    )}
                  </div>

                  {accountSettings && (
                    <form>
                      <div class="single-input-container">
                        {/* the display name belongs to the fan profile; a linked author is shown
                            as their Author, which is managed in the dashboard */}
                        {!identity.official && (
                          <div class="single-input-group">
                            <h5 class="h5 single-h5">{t('perks.displayName')}</h5>
                            <div class="single-input-wrapper">
                              <input
                                id="username"
                                aria-label={t('perks.displayName')}
                                type="text"
                                placeholder={user?.username || t('perks.displayNamePlaceholder')}
                                value={formData.username}
                                onInput={(e) =>
                                  handleInputChange(
                                    'username',
                                    (e.target as HTMLInputElement).value
                                  )
                                }
                              />
                            </div>
                          </div>
                        )}

                        {accountSettings.showPhoneField && (
                          <div class="single-input-group">
                            <h5 class="h5 single-h5">{t('auth.phoneNumber')}</h5>
                            <div class="single-input-wrapper">
                              <div class="phone-with-country-code">
                                <input
                                  type="text"
                                  id="countryCode"
                                  aria-label={t('a11y.countryCode')}
                                  name="countryCode"
                                  value={formData.countryCode}
                                  placeholder="+1"
                                  maxLength={4}
                                  style={{
                                    width: `${Math.max(3, (formData.countryCode?.length ?? 0) + 2)}ch`,
                                  }}
                                  onInput={(e) =>
                                    handleInputChange(
                                      'countryCode',
                                      (e.target as HTMLInputElement).value
                                    )
                                  }
                                />
                                <input
                                  id="phone"
                                  aria-label={t('auth.phoneNumber')}
                                  type="text"
                                  name="customer[note][phone]"
                                  autocomplete="tel"
                                  placeholder={user?.phone || t('auth.phonePlaceholder')}
                                  value={formData.phone}
                                  onInput={handlePhoneInput}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {(accountSettings.showBirthdayField || accountSettings.showZipCodeField) && (
                        <div class="single-input-container">
                          {accountSettings.showBirthdayField && (
                            <div class="single-input-group">
                              <h5 class="h5 single-h5">{t('auth.birthday')}</h5>
                              <div class="single-input-wrapper single-date-wrapper">
                                <input
                                  id="birthday"
                                  aria-label={t('auth.birthday')}
                                  type="date"
                                  placeholder={user?.birthday || '00/00/0000'}
                                  value={formData.birthday}
                                  max={todayMinus18}
                                  onInput={(e) =>
                                    handleInputChange(
                                      'birthday',
                                      (e.target as HTMLInputElement).value
                                    )
                                  }
                                />
                                <span class="single-date-icon" aria-hidden="true">
                                  <IconCalendar width={14} height={14} />
                                </span>
                              </div>
                            </div>
                          )}

                          {accountSettings.showZipCodeField && (
                            <div class="single-input-group">
                              <h5 class="h5 single-h5">{t('auth.zipcodePlaceholder')}</h5>
                              <div class="single-input-wrapper">
                                <input
                                  id="zipCode"
                                  aria-label={t('auth.zipcodePlaceholder')}
                                  type="text"
                                  placeholder={user?.zipCode || '111111'}
                                  value={formData.zipCode}
                                  onInput={(e) =>
                                    handleInputChange(
                                      'zipCode',
                                      (e.target as HTMLInputElement).value
                                    )
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div class="single-input-container single-email-prefs">
                        <h5 class="h5 single-h5">{t('perks.emailPreferences')}</h5>
                        {formData.emailPreferences.map((emailPref, i) => (
                          <div key={i} class="single-input-group single-checkbox">
                            <input
                              id={`emailPref-${i}`}
                              type="checkbox"
                              checked={emailPref.enabled}
                              onChange={(e) =>
                                handleEmailPrefChange(i, (e.target as HTMLInputElement).checked)
                              }
                            />
                            <label for={`emailPref-${i}`}>
                              <span class="single-email-pref-title">
                                {emailPreferenceLabel(emailPref.type)}
                              </span>
                              {emailPreferenceDescription(emailPref.type) && (
                                <span class="single-email-pref-description">
                                  {emailPreferenceDescription(emailPref.type)}
                                </span>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>

                      <div class="single-button-group">
                        <button type="submit" class="button btn" onClick={handleSave}>
                          <span>{t('common.save')}</span>
                        </button>
                        <button class="button btn" onClick={handleCancelEdit}>
                          {t('common.cancel')}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>

            {!showEdit && (
              <button class="button btn single-toggle" onClick={() => setShowEdit(true)}>
                {t('common.edit')}
              </button>
            )}
          </div>

          {/* the membership card is a fan artifact -- an artist holds no membership */}
          {!identity.official && <PerksWalletCard />}
        </div>
      )}

      <div class="single-my-acct-membership-blocks">
        {customer?.customerId ? (
          // a linked author sees this only to manage a contract they already hold: no contract
          // means no section and no Join call to action
          (!identity.official || hasContract) && (
            <MembershipAccountInfo
              showLogin={false}
              showManageButton={true}
              showWalletPassButton={false}
            />
          )
        ) : (
          <div class="membership-login page-width">
            <h2>
              {!anyActiveMemberships && <span>{t('perks.noActiveMemberships')} </span>}
              <span class="single-login-link" onClick={handleOpenLoginModal}>
                {t('auth.logIn')}
              </span>
            </h2>
          </div>
        )}
      </div>
    </div>
  );
};
